/**
 * 语音输入（网页版）：用浏览器自带的语音识别（Web Speech API）。
 *
 * iOS 用的是阿里云百炼的实时识别，要在 WebSocket 握手时带 Authorization 头——浏览器的 WebSocket 不能加请求头，
 * 所以网页版换成浏览器自带的识别：iPhone 上的 Safari 用苹果的服务，电脑 Chrome 用谷歌的服务，Edge 用微软的服务。
 * 字直接写进输入框，点一下开始、再点一下结束；单次最长 3 分钟，切到后台自动结束（和 iOS 一样）。
 * 第一次先单独要麦克风权限；启动不起来、结束收不了尾都有超时兜底，按钮不会卡在“在听”。
 */
interface SpeechRecognitionResultLike { isFinal: boolean; 0: { transcript: string } }
interface SpeechRecognitionEventLike { resultIndex: number; results: ArrayLike<SpeechRecognitionResultLike> }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onaudiostart: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

function recognitionClass(): (new () => SpeechRecognitionLike) | undefined {
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export function dictationSupported(): boolean {
  return typeof window !== "undefined" && recognitionClass() !== undefined;
}

export type DictationState = "idle" | "starting" | "listening";

/** 点了麦克风后多久还没开始收音就算失败（第一次要等用户点“允许”，给足时间） */
const START_TIMEOUT = 15_000;
/** 点了结束后多久还没收尾就直接断开，免得按钮一直卡在“在听” */
const STOP_TIMEOUT = 2_500;

export class Dictation {
  private rec?: SpeechRecognitionLike;
  private base = "";
  private finalText = "";
  private timers: ReturnType<typeof setTimeout>[] = [];
  private micReady = false;
  private stopping = false;
  state: DictationState = "idle";

  constructor(
    private readonly read: () => string,
    private readonly write: (text: string) => void,
    private readonly onChange: (state: DictationState) => void,
    private readonly onError: (message: string) => void,
  ) {}

  get active(): boolean { return this.state !== "idle"; }

  async start(): Promise<void> {
    const Rec = recognitionClass();
    if (!Rec || this.active) return;
    this.setState("starting");
    // 先单独要麦克风权限：浏览器第一次弹“允许使用麦克风”时直接开识别，Safari 常常卡住不出字、Chrome 会白等
    let warmed = false;
    if (!this.micReady && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        this.micReady = true;
        warmed = true;
      } catch (e) {
        const name = e instanceof DOMException ? e.name : "";
        this.onError(name === "NotAllowedError" ? MESSAGES["not-allowed"] : name === "NotFoundError" ? MESSAGES["audio-capture"] : `打不开麦克风：${e instanceof Error ? e.message : String(e)}`);
        this.setState("idle");
        return;
      }
      if (this.state !== "starting") return; // 等权限的时候被取消了
    }
    const rec = new Rec();
    rec.lang = "zh-CN";
    rec.continuous = true;
    rec.interimResults = true;
    this.base = this.read();
    this.finalText = "";
    this.stopping = false;
    rec.onaudiostart = () => { if (this.rec === rec) this.setState("listening"); };
    rec.onresult = (e) => {
      if (this.rec !== rec) return;
      if (this.state === "starting") this.setState("listening");
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) this.finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      // 点了结束后识别服务有时回一个空结果：别把屏幕上已经出来的字清掉
      if (this.stopping && !(this.finalText + interim)) return;
      this.write(this.base + this.finalText + interim);
    };
    rec.onerror = (e) => {
      if (this.rec !== rec) return;
      if (e.error === "no-speech" || e.error === "aborted") return;
      // 刚允许完麦克风：有的浏览器（Safari）要求识别必须由这次点击直接触发，再点一次就好
      if (warmed && e.error === "not-allowed") this.onError("麦克风已经允许了，请再点一次开始说话");
      else this.onError(MESSAGES[e.error] ?? `语音识别出错：${e.error}`);
    };
    rec.onend = () => { if (this.rec === rec) this.finish(); };
    this.rec = rec;
    try {
      rec.start();
    } catch (e) {
      this.onError(warmed ? "麦克风已经允许了，请再点一次开始说话" : (e instanceof Error ? e.message : String(e)));
      this.finish();
      return;
    }
    this.after(START_TIMEOUT, () => {
      if (this.rec !== rec || this.state !== "starting") return;
      this.onError("语音识别没有启动起来，请再点一次麦克风。电脑上的 Chrome 用的是谷歌的识别服务，网络连不上时会这样");
      rec.abort();
      this.finish();
    });
    this.after(3 * 60 * 1000, () => { if (this.rec === rec) this.stop(); });
  }

  /** 结束：已经识别的字保留 */
  stop(): void {
    const rec = this.rec;
    if (!rec) { if (this.state === "starting") this.setState("idle"); return; }
    this.stopping = true;
    rec.stop();
    this.after(STOP_TIMEOUT, () => { if (this.rec === rec) { rec.abort(); this.finish(); } });
  }

  cancel(): void {
    const rec = this.rec;
    this.finish();
    rec?.abort();
  }

  private finish(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.rec = undefined;
    this.setState("idle");
  }

  private after(ms: number, f: () => void): void {
    this.timers.push(setTimeout(f, ms));
  }

  private setState(s: DictationState): void {
    if (this.state === s) return;
    this.state = s;
    this.onChange(s);
  }
}

const MESSAGES: Record<string, string> = {
  "not-allowed": "没有麦克风权限：请在浏览器的网站设置里允许使用麦克风（Mac 上还要在 系统设置 → 隐私与安全性 → 麦克风 里允许浏览器）",
  "service-not-allowed": "这个浏览器不允许网页使用语音识别（iPhone 要在 设置 → 通用 → 键盘 里打开“听写”）",
  network: "浏览器的语音识别服务连不上（Chrome 用的是谷歌的服务，国内网络可能用不了；Safari、Edge 可以试试）",
  "audio-capture": "找不到麦克风",
};
